import { useEffect, useRef, useState, type RefObject } from 'react';

type ScrollFadeState = { fadeTop: boolean; fadeBottom: boolean };

export function useScrollFade(ref: RefObject<HTMLElement | null>, threshold = 5): ScrollFadeState {
    const [state, setState] = useState<ScrollFadeState>({ fadeTop: false, fadeBottom: false });
    const [el, setEl] = useState<HTMLElement | null>(null);
    const rafRef = useRef(0);

    // Sync ref.current → el state so the effect re-runs when the element changes.
    // This handles cases where the ref target unmounts and remounts (e.g. navigating
    // to event detail and back), since React refs don't trigger re-renders.
    useEffect(() => {
        if (ref.current !== el) setEl(ref.current);
    }, [ref, el]);

    useEffect(() => {
        if (!el) {
            setState({ fadeTop: false, fadeBottom: false });
            return undefined;
        }

        const update = () => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const fadeTop = scrollTop > threshold;
            const fadeBottom = scrollTop + clientHeight < scrollHeight - threshold;
            setState(prev =>
                prev.fadeTop === fadeTop && prev.fadeBottom === fadeBottom ? prev : { fadeTop, fadeBottom },
            );
        };

        // Debounce mutation/resize callbacks to one RAF to avoid
        // re-render storms when Sendbird or other libraries do
        // many rapid DOM mutations during initialization.
        const scheduleUpdate = () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(update);
        };

        update();
        el.addEventListener('scroll', update, { passive: true });

        const ro = new ResizeObserver(scheduleUpdate);
        ro.observe(el);

        const mo = new MutationObserver(scheduleUpdate);
        mo.observe(el, { childList: true, subtree: true });

        return () => {
            el.removeEventListener('scroll', update);
            cancelAnimationFrame(rafRef.current);
            ro.disconnect();
            mo.disconnect();
        };
    }, [el, threshold]);

    return state;
}
